import { type RefObject, useEffect, useState } from 'react';

const KEYBOARD_OPEN_THRESHOLD = 120;
const KEYBOARD_CLOSE_THRESHOLD = 60;

function assistantInputIsFocused() {
  return document.activeElement?.id === 'assistant-message';
}

/**
 * Mantiene el alto del asistente alineado con el viewport realmente visible.
 * El foco por sí solo no implica que haya teclado: sólo lo consideramos abierto
 * cuando VisualViewport confirma una reducción material de espacio.
 */
export function useAssistantVisualViewport(
  enabled: boolean,
  shellRef: RefObject<HTMLElement>,
) {
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setKeyboardOpen(false);
      return;
    }

    const shell = shellRef.current;
    if (!shell) return;

    const viewport = window.visualViewport;
    let baselineHeight = viewport?.height ?? window.innerHeight;
    let lastVisibleBottom = viewport
      ? viewport.height + viewport.offsetTop
      : window.innerHeight;
    let open = false;
    let resettingOrientation = false;
    let frame: number | undefined;
    let orientationTimer: number | undefined;

    const visibleBottom = () => {
      if (!viewport) return window.innerHeight;
      if (Math.abs(viewport.scale - 1) > 0.01) return lastVisibleBottom;
      lastVisibleBottom = viewport.height + viewport.offsetTop;
      return lastVisibleBottom;
    };

    const applyViewport = () => {
      frame = undefined;
      const bottom = visibleBottom();
      shell.style.setProperty('--app-visual-height', `${Math.round(bottom)}px`);

      if (resettingOrientation) return;

      // Un pinch zoom también altera VisualViewport. Conservamos la geometría y
      // el estado anteriores hasta volver a escala 1 para no reinsertar la nav.
      if (!viewport || Math.abs(viewport.scale - 1) > 0.01) return;

      const focused = assistantInputIsFocused();
      const occludedHeight = Math.max(0, baselineHeight - viewport.height);
      const nextOpen = focused
        && (open
          ? occludedHeight > KEYBOARD_CLOSE_THRESHOLD
          : occludedHeight >= KEYBOARD_OPEN_THRESHOLD);

      if (nextOpen !== open) {
        open = nextOpen;
        setKeyboardOpen(nextOpen);
      }

      if (!focused) baselineHeight = viewport.height;
    };

    const scheduleViewportUpdate = () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(applyViewport);
    };

    const resetForOrientation = () => {
      resettingOrientation = true;
      if (orientationTimer !== undefined) window.clearTimeout(orientationTimer);
      orientationTimer = window.setTimeout(() => {
        const focused = assistantInputIsFocused();
        const height = viewport?.height ?? window.innerHeight;
        // Una rotación con teclado físico conserva `closed`; con teclado virtual
        // conserva `open` hasta que el viewport se expanda o el campo pierda foco.
        baselineHeight = focused && open
          ? height + KEYBOARD_OPEN_THRESHOLD
          : height;
        resettingOrientation = false;
        scheduleViewportUpdate();
      }, 250);
    };

    applyViewport();
    viewport?.addEventListener('resize', scheduleViewportUpdate);
    viewport?.addEventListener('scroll', scheduleViewportUpdate);
    window.addEventListener('resize', scheduleViewportUpdate);
    window.addEventListener('orientationchange', resetForOrientation);
    document.addEventListener('focusin', scheduleViewportUpdate);
    document.addEventListener('focusout', scheduleViewportUpdate);

    return () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      if (orientationTimer !== undefined) window.clearTimeout(orientationTimer);
      viewport?.removeEventListener('resize', scheduleViewportUpdate);
      viewport?.removeEventListener('scroll', scheduleViewportUpdate);
      window.removeEventListener('resize', scheduleViewportUpdate);
      window.removeEventListener('orientationchange', resetForOrientation);
      document.removeEventListener('focusin', scheduleViewportUpdate);
      document.removeEventListener('focusout', scheduleViewportUpdate);
      shell.style.removeProperty('--app-visual-height');
    };
  }, [enabled, shellRef]);

  return keyboardOpen;
}
